import { useMemo, useState } from 'react';
import { claimPairingCode, unpairDevice } from '../../services/firebase/headsetPairing';

export interface PairingState {
  code: string;
  deviceName: string;
  status: 'idle' | 'loading' | 'error' | 'success';
  message: string;
}

export interface PairingActions {
  setCode: (value: string) => void;
  setDeviceName: (value: string) => void;
  handlePair: () => Promise<void>;
  handleUnpair: (deviceId: string) => Promise<void>;
}

/**
 * Manages pairing UI state and actions.
 */
export function usePairingActions(): { state: PairingState; actions: PairingActions } {
  const [state, setState] = useState<PairingState>({
    code: '',
    deviceName: 'Quest Headset',
    status: 'idle',
    message: '',
  });

  const setCode = (value: string) => setState((current) => ({ ...current, code: value }));
  const setDeviceName = (value: string) =>
    setState((current) => ({ ...current, deviceName: value }));
  const handlePair = () => pairDevice(state, setState);
  const handleUnpair = (deviceId: string) => unpairDeviceAction(deviceId, setState);

  return { state, actions: { setCode, setDeviceName, handlePair, handleUnpair } };
}

/**
 * Builds the pairing header label.
 */
export function usePairingHeader(deviceCount: number): string {
  return useMemo(
    () => (deviceCount > 0 ? `${deviceCount} headset paired` : 'No paired headsets'),
    [deviceCount]
  );
}

async function pairDevice(
  state: PairingState,
  setState: (next: (current: PairingState) => PairingState) => void
): Promise<void> {
  if (!state.code.trim()) {
    setState((current) => ({
      ...current,
      status: 'error',
      message: 'Enter the 4-character pairing code shown in your headset.',
    }));
    return;
  }

  try {
    setState((current) => ({ ...current, status: 'loading', message: '' }));
    await claimPairingCode(state.code.trim(), state.deviceName.trim());
    setState((current) => ({
      ...current,
      code: '',
      status: 'success',
      message: 'Headset paired successfully.',
    }));
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to pair headset.';
    setState((current) => ({ ...current, status: 'error', message }));
  }
}

async function unpairDeviceAction(
  deviceId: string,
  setState: (next: (current: PairingState) => PairingState) => void
): Promise<void> {
  try {
    setState((current) => ({ ...current, status: 'loading', message: '' }));
    await unpairDevice(deviceId);
    setState((current) => ({
      ...current,
      status: 'success',
      message: 'Headset unpaired.',
    }));
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to unpair headset.';
    setState((current) => ({ ...current, status: 'error', message }));
  }
}
